"use client";

// Libraries
import Head from "next/head";
import { useForm } from "react-hook-form";
import { Link, useRouter } from "@/i18n/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getProviders, signIn, useSession } from "next-auth/react";
import moment from "moment-timezone";
import posthog from "posthog-js";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

// Components
import Layout from "@/components/ui/Layout/Layout";
import Button from "@/components/ui/Button/Button";
import Image from "next/image";
import { sha256 } from "js-sha256";
import { sendGTMEvent } from "@next/third-parties/google";
import { useSearchParams } from "next/navigation";
import Logo from "../../ui/Logo/Logo";
import { LoginForm } from "@/components/login-form";
import { useLocale, useTranslations } from "next-intl";
import { sendMetaEvent } from "@/lib/tracking/metaEvent";

// Inner component that uses reCAPTCHA hooks
function SignUpFormInner({ session, source, playgroundSlug = null }) {
    // Variables
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm();
    const router = useRouter();
    const searchParams = useSearchParams();
    const queryWaiting = searchParams.get("waiting");
    const queryCourseImage = searchParams.get("courseImage");
    const queryCourseSlug = searchParams.get("courseSlug");
    const queryError = searchParams.get("error");

    // Pré-remplissage depuis le lien de conversion invité → compte
    // (le CTA d'un appel invité passe ?firstName&lastName&email).
    useEffect(() => {
        const f = searchParams.get("firstName");
        const l = searchParams.get("lastName");
        const e = searchParams.get("email");
        if (f) setValue("firstname", f);
        if (l) setValue("lastname", l);
        if (e) setValue("email", e);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const locale = useLocale();
    const t = useTranslations();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const recaptchaV2Ref = useRef(null);

    if (session) {
        router.push("/");
    }

    // States
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState();
    const [emailService, setEmailService] = useState();
    const [isRegistered, setIsRegistered] = useState(false);
    const [firstName, setFirstName] = useState();
    const [isFromCoursePage, setIsFromCoursePage] = useState(false);
    const [courseImage, setCourseImage] = useState();
    const [courseSlug, setCourseSlug] = useState(null);
    const [waiting, setWaiting] = useState(false);
    const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
    const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
    const [pendingFormData, setPendingFormData] = useState(null);
    // Le bouton Apple ne s'affiche que si le provider est configuré côté
    // serveur (les 4 variables d'env APPLE_* présentes) : on interroge
    // NextAuth via getProviders au montage.
    const [appleAvailable, setAppleAvailable] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getProviders()
            .then((providers) => {
                if (!cancelled) {
                    setAppleAvailable(!!providers?.apple);
                }
            })
            .catch(() => {
                // Impossible de récupérer les providers : pas de bouton Apple
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Handle reCAPTCHA v2 callback
    const handleRecaptchaV2Change = useCallback((token) => {
        setRecaptchaV2Token(token);
    }, []);

    // Submit with reCAPTCHA token
    const submitWithRecaptcha = useCallback(
        async (data, recaptchaToken, recaptchaVersion) => {
            data.recaptchaToken = recaptchaToken;
            data.recaptchaVersion = recaptchaVersion;

            const response = await fetch("/api/security/signup", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            const responseData = await response.json();

            if (!response.ok) {
                // Handle low score - show v2 captcha
                if (responseData.message === "RECAPTCHA_LOW_SCORE") {
                    setPendingFormData(data);
                    setShowRecaptchaV2(true);
                    setIsLoading(false);
                    toast.info(t("signup_recaptcha_verify"));
                    return false;
                }

                // Handle other reCAPTCHA errors
                if (
                    responseData.message === "RECAPTCHA_FAILED" ||
                    responseData.message === "RECAPTCHA_REQUIRED"
                ) {
                    setIsLoading(false);
                    toast.error(t("signup_recaptcha_error"));
                    return false;
                }

                setIsLoading(false);
                toast.error(responseData.message);
                return false;
            }

            return true;
        },
        [t],
    );

    // Methods
    const onSubmit = async (data) => {
        if (!isLoading) {
            setIsLoading(true);

            // Add timezone using moment
            data.timezone = moment.tz.guess();

            // Detect email service used
            const email = data.email;
            const emailService = email.substring(email.lastIndexOf("@") + 1);
            setEmailService(emailService);
            setFirstName(data.firstname);

            // Add locale for data
            data.locale = locale;
            data.source = source;
            data.playgroundSlug = playgroundSlug;

            // Add course image and slug if user comes from course page
            if (courseSlug) {
                data.courseSlug = courseSlug;
            }

            // Get reCAPTCHA v3 token
            if (!executeRecaptcha) {
                setIsLoading(false);
                toast.error(t("signup_recaptcha_error"));
                return;
            }

            try {
                const recaptchaToken = await executeRecaptcha("signup");
                const success = await submitWithRecaptcha(
                    data,
                    recaptchaToken,
                    "v3",
                );

                if (!success) {
                    return;
                }
            } catch (error) {
                setIsLoading(false);
                toast.error(t("signup_recaptcha_error"));
                return;
            }

            // setIsLoading(false);
            setError(null);
            // gtag_report_conversion();
            sendGTMEvent({
                event: "user_signup",
            });

            // Le pixel du navigateur ET l'API Conversions, sous le même
            // identifiant d'événement : les deux envois séparés faisaient
            // compter l'inscription deux fois (lib/tracking/metaEvent.js).
            sendMetaEvent("CompleteRegistration");

            // Connect user
            let tmpUrl = "/verify-account";
            if (locale !== "fr") {
                tmpUrl = `/${locale}/verify-account`;
            }

            posthog.capture("user_signup");
            await signIn("credentials", {
                email: data.email,
                password: data.password,
                callbackUrl: tmpUrl,
            });
            return;
        }
    };

    // Handle v2 reCAPTCHA submission
    const handleRecaptchaV2Submit = useCallback(async () => {
        if (!recaptchaV2Token || !pendingFormData) {
            toast.error(t("signup_recaptcha_required"));
            return;
        }

        setIsLoading(true);

        const success = await submitWithRecaptcha(
            pendingFormData,
            recaptchaV2Token,
            "v2",
        );

        if (!success) {
            // Reset v2 captcha
            if (recaptchaV2Ref.current) {
                recaptchaV2Ref.current.reset();
            }
            setRecaptchaV2Token(null);
            return;
        }

        // Success - continue with post-signup flow
        setError(null);
        sendGTMEvent({
            event: "user_signup",
        });

        // Pixel et API Conversions d'un seul geste (voir bloc du dessus).
        sendMetaEvent("CompleteRegistration");

        // Connect user
        let tmpUrl = "/verify-account";
        if (locale !== "fr") {
            tmpUrl = `/${locale}/verify-account`;
        }

        posthog.capture("user_signup");
        await signIn("credentials", {
            email: pendingFormData.email,
            password: pendingFormData.password,
            callbackUrl: tmpUrl,
        });
    }, [
        recaptchaV2Token,
        pendingFormData,
        submitWithRecaptcha,
        locale,
        t,
    ]);

    const handleGoogleSignUp = async () => {
        if (!isLoading) {
            setIsLoading(true);

            // gtag_report_conversion();
            let tmpUrl = courseSlug
                ? `/onboarding?courseSlug=${courseSlug}&firstTime=true`
                : "/onboarding?firstTime=true";

            // Ajouter le paramètre source s'il existe
            if (source) {
                tmpUrl += `&source=${source}`;
            }

            if (locale !== "fr") {
                tmpUrl = `/${locale}/${tmpUrl}`;
            }

            // Connect user
            posthog.capture("user_signup");
            sendGTMEvent({ event: "user_signup" });
            if (typeof window !== "undefined" && window.fbq) {
                window.fbq("track", "CompleteRegistration");
            }
            await signIn("google", {
                // redirect: false,
                callbackUrl: tmpUrl,
            });
        }
    };

    // Calqué sur handleGoogleSignUp (mêmes URL de retour et trackings),
    // en remplaçant simplement le provider par Apple.
    const handleAppleSignUp = async () => {
        if (!isLoading) {
            setIsLoading(true);

            // gtag_report_conversion();
            let tmpUrl = courseSlug
                ? `/onboarding?courseSlug=${courseSlug}&firstTime=true`
                : "/onboarding?firstTime=true";

            // Ajouter le paramètre source s'il existe
            if (source) {
                tmpUrl += `&source=${source}`;
            }

            if (locale !== "fr") {
                tmpUrl = `/${locale}/${tmpUrl}`;
            }

            // Connect user
            posthog.capture("user_signup");
            sendGTMEvent({ event: "user_signup" });
            if (typeof window !== "undefined" && window.fbq) {
                window.fbq("track", "CompleteRegistration");
            }
            await signIn("apple", {
                // redirect: false,
                callbackUrl: tmpUrl,
            });
        }
    };

    useEffect(() => {
        if (queryWaiting) {
            setWaiting(true);
        }

        if (queryCourseImage) {
            setIsFromCoursePage(true);
            setCourseImage(queryCourseImage);
        }

        if (queryCourseSlug) {
            setCourseSlug(queryCourseSlug);
        }

        if (queryError) {
            let message = "";
            switch (queryError) {
                case "EMAIL_ALREADY_USED":
                    message = t("login_emailAlreadyUsed");
                    break;
                case "GOOGLE_DISCONNECTED":
                    message = t("login_googleDisconnected");
                    break;
                default:
                    console.log(
                        "Erreur pendant inscription Google.com",
                        queryError,
                    );
                    message = t("login_unknowedError");
                    break;
            }
            toast.error(
                queryError == "Callback" ? t("login_unknowedError") : message,
            );
        }
    }, []);

    return (
        <div className="flex min-h-svh flex-col items-center justify-center md:bg-muted p-6 md:p-10">
            <div className="w-full max-w-sm md:max-w-3xl">
                <LoginForm
                    handleGoogleSignUp={handleGoogleSignUp}
                    handleAppleSignUp={handleAppleSignUp}
                    appleAvailable={appleAvailable}
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    register={register}
                    errors={errors}
                    isLoading={isLoading}
                    source={source}
                    showRecaptchaV2={showRecaptchaV2}
                    onRecaptchaV2Change={handleRecaptchaV2Change}
                    onRecaptchaV2Submit={handleRecaptchaV2Submit}
                    recaptchaV2Ref={recaptchaV2Ref}
                    recaptchaV2Token={recaptchaV2Token}
                />
            </div>
        </div>
    );
}

// Main component with reCAPTCHA provider
export default function Login({
    session,
    source,
    playgroundSlug = null,
    ...props
}) {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY}
            scriptProps={{
                async: true,
                defer: true,
            }}
        >
            <SignUpFormInner
                session={session}
                source={source}
                playgroundSlug={playgroundSlug}
            />
        </GoogleReCaptchaProvider>
    );
}
